import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ChevronLeft, Pin, PinOff, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import type { Machine } from "@/machines/types/machine.ts";
import type { MachineKpi } from "@/map/hooks/useMachineKpis";
import { useMapPinStore } from "@/map/stores/useMapPinStore";
import { formatOrgLocaleTime } from "@/shared/utils/date/org-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";

/** seconds → "X min" (mirrors KPIStripe's formatTime). */
const formatCycle = (t: TFunction<"map">, seconds?: number): string => {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  return t("machineRail.minutes", { value: formatNumber(Math.round(seconds / 60)) });
};

/** vsTarget seconds delta → signed "+/-Xs", or "—". */
const formatVsTarget = (seconds?: number): string => {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const rounded = Math.round(seconds);
  return `${rounded > 0 ? "+" : ""}${formatNumber(rounded)}s`;
};

const formatLastSeen = (lastSeen: Machine["last_seen"]): string => {
  if (!lastSeen) return "—";
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return "—";
  return formatOrgLocaleTime(d, { hour: "2-digit", minute: "2-digit", hour12: false });
};

interface MachineRailCardProps {
  machine: Machine;
  kpi?: MachineKpi;
  isSelected: boolean;
  onSelect: () => void;
}

const MachineRailCard = ({ machine, kpi, isSelected, onSelect }: MachineRailCardProps) => {
  const { t } = useTranslation("map");
  const isOnline = machine.status === "online";

  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-xl border bg-background/95 backdrop-blur-sm shadow-md p-3 transition-all hover:border-foreground/30",
        isSelected ? "border-foreground/60 ring-1 ring-foreground/20" : "border-border",
      )}
      style={{ borderLeftWidth: 4, borderLeftColor: machine.color || "#EF4444" }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "h-2.5 w-2.5 rounded-full shrink-0",
              isOnline ? "bg-green-500" : "bg-red-500",
            )}
          />
          <span className="font-semibold text-sm truncate">{machine.name}</span>
        </div>
        <span className="text-[11px] text-muted-foreground font-mono shrink-0">
          {formatLastSeen(machine.last_seen)}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-md bg-muted/60 px-2 py-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("machineRail.cycle")}</div>
          <div className="text-xs font-medium font-mono">
            {kpi?.isLoading ? "…" : formatCycle(t, kpi?.avgCycleTime)}
          </div>
        </div>
        <div className="rounded-md bg-muted/60 px-2 py-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("machineRail.vsTarget")}</div>
          <div className="text-xs font-medium font-mono">
            {kpi?.isLoading ? "…" : formatVsTarget(kpi?.vsTarget)}
          </div>
        </div>
      </div>
    </button>
  );
};

interface MachineRailProps {
  locationName: string | null;
  /** All selectable locations — drives the header location switcher. */
  locations: { id: number; name: string }[];
  selectedLocationId: number | null;
  onSelectLocation: (id: number) => void;
  machines: Machine[];
  kpisByDevice: Record<string, MachineKpi>;
  selectedMachineId: number | null;
  /** Show the "All locations" control (only in the zoomed-in location view). */
  showBack: boolean;
  /** Slide the rail right of the left-docked video column (review mode). */
  offsetForVideo?: boolean;
  onSelectMachine: (id: number) => void;
  onBack: () => void;
}

/**
 * Floating stack of machine cards over the left edge of the map (not a fixed
 * sidebar). Includes the location header and the "All locations" back control.
 * Collapsible (session-only) so it can be tucked away — e.g. beside the
 * left-docked video in review mode.
 */
const MachineRail = ({
  locationName,
  locations,
  selectedLocationId,
  onSelectLocation,
  machines,
  kpisByDevice,
  selectedMachineId,
  showBack,
  offsetForVideo = false,
  onSelectMachine,
  onBack,
}: MachineRailProps) => {
  const { t } = useTranslation("map");
  const [collapsed, setCollapsed] = useState(false);
  const leftClass = offsetForVideo ? "left-[356px]" : "left-4";

  // Layout lock: freeze the card ORDER at pin time so a data refetch (machines
  // reload every minute, and the backend may re-sort by status/last-seen) can't
  // reshuffle the rail. Metrics/status inside each card still update live —
  // only the sequence is frozen. Unpinning resumes the incoming order.
  const isPinned = useMapPinStore((s) => s.isPinned("machineRail"));
  const togglePin = useMapPinStore((s) => s.togglePin);
  const [frozenOrder, setFrozenOrder] = useState<number[] | null>(null);
  useEffect(() => {
    if (isPinned && frozenOrder == null) setFrozenOrder(machines.map((m) => m.id));
    else if (!isPinned && frozenOrder != null) setFrozenOrder(null);
  }, [isPinned, machines, frozenOrder]);

  const orderedMachines = useMemo(() => {
    if (!frozenOrder) return machines;
    const byId = new Map(machines.map((m) => [m.id, m]));
    // Keep the frozen sequence for machines still present; append any that
    // appeared after pinning at the end (never drop live data).
    const inOrder = frozenOrder
      .map((id) => byId.get(id))
      .filter((m): m is Machine => m != null);
    const extras = machines.filter((m) => !frozenOrder.includes(m.id));
    return [...inOrder, ...extras];
  }, [machines, frozenOrder]);

  if (collapsed) {
    return (
      <div className={cn("absolute top-4 z-10", leftClass)}>
        <button
          onClick={() => setCollapsed(false)}
          aria-label={t("machineRail.showMachines")}
          title={t("machineRail.showMachines")}
          className="flex items-center gap-1.5 rounded-lg border bg-background/90 px-3 py-1.5 text-sm font-medium shadow-sm backdrop-blur-sm transition-colors hover:bg-background"
        >
          <PanelLeftOpen className="h-4 w-4" />
          {t("machineRail.machines")}
        </button>
      </div>
    );
  }

  return (
    <div className={cn("absolute top-4 z-10 flex max-h-[calc(100%-2rem)] w-60 flex-col gap-2", leftClass)}>
      {showBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 self-start rounded-lg border bg-background/90 px-3 py-1.5 text-sm font-medium shadow-sm backdrop-blur-sm transition-colors hover:bg-background"
        >
          <ChevronLeft className="h-4 w-4" />
          {t("machineRail.allLocations")}
        </button>
      )}

      {/* Location header — a switcher, so the location can be changed directly */}
      <div className="rounded-xl border bg-background/95 px-3 py-2 shadow-md backdrop-blur-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("machineRail.location")}</div>
            <Select
              value={selectedLocationId != null ? String(selectedLocationId) : undefined}
              onValueChange={(v) => onSelectLocation(Number(v))}
            >
              <SelectTrigger className="mt-0.5 h-7 w-full border-0 bg-transparent p-0 text-sm font-semibold shadow-none focus:ring-0 focus-visible:ring-0 [&>span]:truncate">
                <SelectValue placeholder={t("machineRail.selectLocation")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {locations.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              {locationName
                ? t("machineRail.machineCount", { count: machines.length })
                : t("machineRail.selectLocation")}
            </div>
          </div>
          <div className="-mr-1 flex shrink-0 items-center">
            <button
              onClick={() => togglePin("machineRail")}
              aria-label={isPinned ? t("machineRail.unpin") : t("machineRail.pin")}
              title={isPinned ? t("machineRail.unpin") : t("machineRail.pin")}
              className={cn(
                "rounded-md p-1 transition-colors hover:bg-muted hover:text-foreground",
                isPinned ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setCollapsed(true)}
              aria-label={t("machineRail.hideMachines")}
              title={t("machineRail.hideMachines")}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Floating machine cards */}
      <div className="flex flex-col gap-2 overflow-y-auto pr-1">
        {orderedMachines.map((machine) => (
          <MachineRailCard
            key={machine.id}
            machine={machine}
            kpi={kpisByDevice[machine.device_mxid]}
            isSelected={machine.id === selectedMachineId}
            onSelect={() => onSelectMachine(machine.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default MachineRail;
